import { useState, useRef, useEffect } from 'react';
import { useFlatsDataContext } from '../../../../../../../../contexts/FlatsDataContext';
import { paths } from '../../../../../../../../settings';

import styles from './FlatFloorFilter.module.css';

type TFloorOption = {
    value: string;
    label: string;
};

const FloorFilter: React.FC<{ floors: TFloorOption[], onSelectFloor: (floor: string) => void }> = ({ floors, onSelectFloor }): JSX.Element => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [selectedFloorDropdown, setSelectedFloorDropdown] = useState<string>('Wybierz');
    const { contextSelectedFloor } = useFlatsDataContext();
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        findAndSelectDropdownFloor();
    }, [contextSelectedFloor]);

    /**
     * Toggles the dropdown menu's open/closed state.
     * @returns {void}
    */
    const toggleDropdown = (): void => setIsOpen(!isOpen);

    /**
     * Finds the currently selected Floor in the dropdown options and selects it.
     * If a Floor is already selected in Context, it matches it with an option in the dropdown and triggers selection.
     * @returns {void}
    */
    const findAndSelectDropdownFloor = (): void => {
        if (contextSelectedFloor !== '') {
            const selectedOption = floors.find(floor => floor.value === contextSelectedFloor);
            if (selectedOption) {
                handleSelectFloor(selectedOption.value, selectedOption.label);
            }
        }
    }

    /**
     * Handles selecting a Floor from the dropdown, setting the selected Floor label in the dropdown,
     * Calling the provided `onSelectFloor` callback, and closing the dropdown.
     * @param {string} floorValue - The value representing the selected Floor.
     * @param {string} floorLabel - The display label for the selected Floor.
     * @returns {void}
    */
    const handleSelectFloor = (floorValue: string, floorLabel: string): void => {
        setSelectedFloorDropdown(floorLabel); 
        onSelectFloor(floorValue);
        setIsOpen(false);
    };

    return (
        <div className={styles.floorFilter} ref={dropdownRef}>
            <span className={styles.label}>Piętro</span>
            <div className={styles.dropdownHeader} onClick={toggleDropdown}>
                {selectedFloorDropdown}
                <img 
                    src={`${paths.assets}/icons/${isOpen ? 'arrow_up' : 'arrow_down'}.png`} 
                    alt={isOpen ? 'Zwiń' : 'Rozwiń'} 
                    width={24} 
                    height={24} 
                    className={styles.arrow}
                />
            </div>
            {isOpen && (
                <div className={styles.dropdownList}>
                    {floors.map((floor, index) => (
                        <div
                            key={index}
                            className={styles.dropdownItem}
                            onClick={() => handleSelectFloor(floor.value, floor.label)}
                        >
                            {floor.label}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default FloorFilter;